<template>
  <v-app class="consulta-ltv-page">
    <v-container>
      <v-col cols="12">
        <v-card elevation="0" class="px-5 py-3">
          <v-card-title class="px-0 pt-0">Consultar LTV</v-card-title>
          <v-row align="end">
            <v-col cols="12" sm="8" md="5">
              <span>Contrato</span>
              <v-text-field
                v-model="contrato"
                label="Digite o contrato"
                dense
                solo-inverted
                clearable
                :disabled="loading"
                @keyup.enter="consultarLtv"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" md="2">
              <span>&nbsp;</span>
              <v-btn
                color="#4682B4"
                block
                height="40"
                style="font-size: 14px; font-weight: bold"
                :loading="loading"
                :disabled="!contratoValido"
                @click="consultarLtv"
              >
                Pesquisar
                <v-icon right dark>mdi-magnify</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-container>

    <v-container class="result-section" fluid tag="section">
      <v-alert v-if="mensagem" type="error" dense class="mb-4">
        {{ mensagem }}
      </v-alert>

      <v-card v-if="ltv" elevation="0" class="pa-5 ltv-result-card">
        <div class="d-flex flex-wrap align-center mb-4">
          <div>
            <v-card-title class="px-0 pt-0 pb-1">
              Resultado do LTV
            </v-card-title>
            <div class="body-2 grey--text text--darken-1">
              Contrato {{ contratoPesquisado }}
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-chip :color="corSeveridade" dark class="font-weight-bold">
            Severidade {{ valorTexto(ltv.severidade) }}
          </v-chip>
        </div>

        <v-row dense>
          <v-col
            v-for="item in indicadores"
            :key="item.label"
            cols="12"
            sm="6"
            md="3"
          >
            <div class="metric-box">
              <div class="metric-label">{{ item.label }}</div>
              <div class="metric-value">{{ item.value }}</div>
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-5"></v-divider>

        <v-row>
          <v-col cols="12" md="7">
            <div class="subtitle-2 font-weight-bold mb-2">
              Oferta sugerida
            </div>
            <div class="offer-box">
              {{ valorTexto(ltv.oferta) }}
            </div>
          </v-col>

          <v-col cols="12" md="5" v-if="dadosConsulta">
            <div class="subtitle-2 font-weight-bold mb-2">
              Dados usados no cálculo
            </div>
            <v-simple-table dense class="ltv-table">
              <tbody>
                <tr>
                  <td>Tempo de casa</td>
                  <td>{{ valorTexto(dadosConsulta.tempoCasa) }}</td>
                </tr>
                <tr>
                  <td>Tipo de contrato</td>
                  <td>{{ valorTexto(dadosConsulta.tipoContrato) }}</td>
                </tr>
                <tr>
                  <td>Receita</td>
                  <td>{{ moeda(dadosConsulta.receita) }}</td>
                </tr>
                <tr>
                  <td>Serviços</td>
                  <td>{{ valorTexto(dadosConsulta.servicos) }}</td>
                </tr>
                <tr>
                  <td>Padrão serviço</td>
                  <td>{{ moeda(dadosConsulta.padraoServico) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-col>
        </v-row>
      </v-card>

      <div v-else-if="!mensagem && !loading" class="empty-state">
        <v-icon large color="blue-grey lighten-2">mdi-chart-box-outline</v-icon>
        <div class="mt-2">Informe um contrato para calcular o LTV.</div>
      </div>
    </v-container>
  </v-app>
</template>

<script>
import Cliente from "@/services/cliente";

export default {
  name: "ConsultaLtv",
  data: () => ({
    contrato: "",
    contratoPesquisado: "",
    dadosConsulta: null,
    ltv: null,
    loading: false,
    mensagem: "",
  }),
  computed: {
    contratoValido() {
      return !!(this.contrato && this.contrato.trim());
    },
    indicadores() {
      if (!this.ltv) return [];

      return [
        {
          label: "LTV",
          value: this.moeda(this.ltv.ltv),
        },
        {
          label: "Débito cliente",
          value: this.moeda(this.ltv.debitoCliente),
        },
        {
          label: "% aplicado",
          value: this.valorTexto(this.ltv.percentualAplicado),
        },
        {
          label: "Valor final",
          value: this.moeda(this.ltv.valorFinal),
        },
        {
          label: "Desconto",
          value: this.moeda(this.ltv.desconto),
        },
      ];
    },
    corSeveridade() {
      const severidade = Number(String(this.ltv?.severidade || "0").replace("%", ""));

      if (severidade > 50) return "red darken-1";
      if (severidade > 30) return "orange darken-1";
      return "green darken-1";
    },
  },
  methods: {
    async consultarLtv() {
      if (!this.contratoValido) return;

      this.loading = true;
      this.mensagem = "";
      this.ltv = null;
      this.dadosConsulta = null;

      const contrato = this.contrato.trim();

      try {
        const response = await Cliente.consultarLtv(contrato);
        const data = response.data;

        if (!data || data.error) {
          this.mensagem =
            data?.message || "Nenhuma informação encontrada para o contrato.";
          return;
        }

        this.contratoPesquisado = data.contrato || contrato;
        this.dadosConsulta = data.dadosConsulta || null;
        this.ltv = data.ltv || data;
      } catch (e) {
        this.mensagem =
          e?.response?.data?.message ||
          "Erro ao calcular LTV. Tente novamente.";
      } finally {
        this.loading = false;
      }
    },
    moeda(valor) {
      const numero = Number(valor || 0);

      return numero.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    valorTexto(valor) {
      if (valor === null || valor === undefined || valor === "") return "-";
      return valor;
    },
  },
};
</script>

<style scoped>
.consulta-ltv-page {
  background-color: #f8f9fb;
}

.result-section {
  background-color: #eee;
  min-height: calc(100vh - 170px);
  padding-top: 24px;
}

.ltv-result-card {
  border: 1px solid #e2e8f0;
}

.metric-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  min-height: 86px;
  padding: 14px;
}

.metric-label {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.metric-value {
  color: #1f2937;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.35;
  margin-top: 8px;
  overflow-wrap: anywhere;
}

.offer-box {
  background: #f8fafc;
  border-left: 4px solid #4682b4;
  border-radius: 6px;
  color: #1f2937;
  line-height: 1.6;
  padding: 16px;
}

.ltv-table {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.ltv-table td:first-child {
  color: #64748b;
  font-weight: 700;
  width: 42%;
}

.empty-state {
  color: #64748b;
  padding: 48px 16px;
  text-align: center;
}
</style>
